import { lazy, type ComponentType } from "react";

declare global {
  interface Window {
    /**
     * Set by the build-time prerenderer (scripts/prerender.ts). When present,
     * `prefetchRoutes()` is a no-op — so the prerendered HTML is not bloated
     * with app-wide <link rel="modulepreload"> hints.
     */
    __PRERENDER__?: boolean;
  }
}

/** Route-component import factories, collected so they can be prefetched together. */
const routeLoaders: Array<() => Promise<unknown>> = [];

/**
 * A dynamic-import rejection that means the requested chunk no longer exists on
 * the server — i.e. a new deploy replaced the hashed filenames the running tab
 * still references. The browser message differs per engine:
 *   - Chrome/Edge: "Failed to fetch dynamically imported module"
 *   - Firefox:     "error loading dynamically imported module"
 *   - Safari:      "Importing a module script failed", or — when the SPA
 *                  fallback serves index.html for the missing chunk —
 *                  "'text/html' is not a valid JavaScript MIME type"
 *                  / "Failed to load module script" (TODOWITH-AI-FRONTEND-1J).
 */
const CHUNK_LOAD_ERROR =
  /dynamically imported module|Importing a module script failed|Failed to (fetch|load) (dynamically imported |)module|Failed to fetch.*\.js|not a valid JavaScript MIME type/i;

/** sessionStorage flag so the post-deploy reload can fire at most once per tab. */
const CHUNK_RELOAD_FLAG = "chunk-reload-once";

export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return CHUNK_LOAD_ERROR.test(msg);
}

/**
 * Wrap a lazy import so a stale-chunk failure self-heals: retry the import
 * once, and if it still fails, force a single full reload to fetch the new
 * deploy's HTML + chunk manifest. The sessionStorage guard ensures we can
 * never loop if the reload doesn't fix it.
 */
async function importWithChunkRecovery<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): Promise<{ default: T }> {
  try {
    const mod = await factory();
    // Re-arm for a future deploy: clearing only on a *successful* import is
    // loop-safe — a still-broken chunk never reaches here, so it can never
    // trigger a second reload, but a later deploy in the same tab can heal.
    if (typeof window !== "undefined" && sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    }
    return mod;
  } catch (err) {
    if (!isChunkLoadError(err)) throw err;
    try {
      return await factory();
    } catch (retryErr) {
      if (
        isChunkLoadError(retryErr) &&
        typeof window !== "undefined" &&
        !sessionStorage.getItem(CHUNK_RELOAD_FLAG)
      ) {
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
        window.location.reload();
      }
      throw retryErr;
    }
  }
}

/**
 * Like `React.lazy`, but also registers the import factory for idle prefetching.
 *
 * Use for route/page components. After first paint, `prefetchRoutes()` warms
 * every registered chunk, so in-app navigation resolves its lazy component
 * synchronously — no `<Suspense>` fallback, no blank-screen flash.
 */
export function lazyRoute<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  routeLoaders.push(factory);
  return lazy(() => importWithChunkRecovery(factory));
}

/**
 * Warm every route chunk once the browser is idle. Safe to call once at startup —
 * the module system deduplicates the `import()` requests. A failed prefetch is
 * non-fatal: the route still lazy-loads on demand.
 */
export function prefetchRoutes(): void {
  // Skip during build-time prerendering: the prerenderer visits every route
  // directly, so warming chunks there only bakes app-wide modulepreload hints
  // into the captured static HTML.
  if (typeof window !== "undefined" && window.__PRERENDER__) return;

  const warm = () => {
    for (const load of routeLoaders) {
      load().catch(() => {
        /* prefetch failure is non-fatal — the route lazy-loads on navigation */
      });
    }
  };

  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(warm);
  } else {
    setTimeout(warm, 2000);
  }
}
